/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import dayjs from 'dayjs';
import React from 'react';
import { toast } from 'react-toastify';

import {
  Button,
  Confirm,
  Dimmer,
  Header,
  Icon,
  Label,
  Loader,
  Message,
  Segment,
  Sidebar,
  Table,
} from '@mairistem/composants';
import { request } from '@mairistem/tools';

import {
  getInseePacsTransfert,
  getInseePacsTransferts,
  getInseePacsTransfertXml,
} from '@/routes';
import type { EnvoiInsee, TransfertInsee } from '@/types';

import { stylesCorps } from './styles';
import type { VueInsee } from './types';

type CouleurLabel = React.ComponentProps<typeof Label>['color'];

/** Statut SDFI d'un lot réceptionné par l'INSEE */
const STATUT_TRAITE = 99;

const STATUTS: Record<number, { libelle: string; couleur: CouleurLabel }> = {
  // Tentative en cours, ou interrompue avant la réponse de l'INSEE
  1: { libelle: 'Envoi en cours', couleur: 'blue' },
  2: { libelle: 'En attente de relance', couleur: 'orange' },
  3: { libelle: 'En erreur', couleur: 'red' },
  5: { libelle: 'En erreur', couleur: 'red' },
  [STATUT_TRAITE]: { libelle: "Réceptionné par l'INSEE", couleur: 'green' },
};

/** Bouton présenté comme un lien Semantic */
const STYLE_LIEN: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: '#4183c4',
  cursor: 'pointer',
};

/** Accusé de réception XML de l'INSEE, et non réponse d'erreur du SDFI */
const estAccuseXml = (valeur: string | null) =>
  _.startsWith(_.trim(valeur ?? ''), '<');

const formaterDate = (date: string | null | undefined) =>
  date ? dayjs(date).format('DD/MM/YYYY') : '';

const messageErreur = (error: any) =>
  error?.response?.data?.message ?? error?.data?.message ?? null;

/**
 * Indentation d'un XML pour la lecture : l'archive et l'accusé de réception
 * sont stockés sur une seule ligne.
 */
export const formaterXml = (xml: string) => {
  let niveau = 0;

  return _.trim(xml)
    .replace(/>\s*</g, '>\n<')
    .split('\n')
    .map((ligne) => {
      if (/^<\//.test(ligne)) niveau = Math.max(niveau - 1, 0);
      const indentee = `${'  '.repeat(niveau)}${ligne}`;
      // Balise ouvrante seule sur sa ligne : le contenu suivant est indenté
      if (/^<[^!?/][^>]*>$/.test(ligne) && !/\/>$/.test(ligne)) niveau += 1;
      return indentee;
    })
    .join('\n');
};

type Consultation = { titre: string; sousTitre: string; contenu: string };

interface InseeHistoriqueProps {
  /** Historique rechargé à chaque activation (ouverture de la modale, affichage de la page) */
  actif: boolean;
  /** Page du menu : le tableau occupe toute la hauteur */
  pleinePage?: boolean;
  /** Appelé après une dévalidation, pour rafraîchir le compteur du tableau de bord */
  onChange?: () => void;
  /** Mise en page : modale du tableau de bord ou page du menu */
  children: (vue: VueInsee) => React.ReactElement;
}

/**
 * Historique des transferts INSEE des PACS, comme l'écran « Historique des
 * transferts INSEE » de WinDev : chaque lot avec ses tentatives de dépôt, et
 * les actions consulter le fichier et dévalider (le renvoi reste automatique, à J+1).
 */
const InseeHistorique = ({
  actif: open,
  pleinePage = false,
  onChange,
  children: mettreEnPage,
}: InseeHistoriqueProps) => {
  const styles = stylesCorps(pleinePage);
  const [transferts, setTransferts] = React.useState<TransfertInsee[] | null>(
    null,
  );
  const [loading, setLoading] = React.useState(false);
  const [deplies, setDeplies] = React.useState<number[]>([]);
  const [enCours, setEnCours] = React.useState<number | null>(null);
  const [aDevalider, setADevalider] = React.useState<TransfertInsee | null>(
    null,
  );
  const [consultation, setConsultation] = React.useState<Consultation | null>(
    null,
  );
  const [panneauOuvert, setPanneauOuvert] = React.useState(false);

  const charger = React.useCallback((premierChargement = false) => {
    setLoading(true);
    request
      .get(getInseePacsTransferts())
      .then((reponse: any) => {
        const lots: TransfertInsee[] = reponse?.data ?? [];
        setTransferts(lots);
        // Comme WinDev : le dernier lot est déplié à l'ouverture
        if (premierChargement) {
          setDeplies(_.compact([lots[0]?.identifiant]));
        }
      })
      .catch(() => {
        toast.error(
          "Une erreur est survenue lors de la récupération de l'historique INSEE",
        );
      })
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    if (open) {
      setPanneauOuvert(false);
      charger(true);
    }
  }, [open, charger]);

  const basculer = React.useCallback((identifiant: number) => {
    setDeplies((prev) => _.xor(prev, [identifiant]));
  }, []);

  const afficher = React.useCallback((doc: Consultation) => {
    setConsultation(doc);
    setPanneauOuvert(true);
  }, []);

  const afficherFichier = React.useCallback(
    (transfert: TransfertInsee) => {
      setEnCours(transfert.identifiant);
      request
        .get(getInseePacsTransfertXml(transfert.identifiant))
        .then((contenu: any) =>
          afficher({
            titre: `Fichier du lot n°${transfert.numero}`,
            sousTitre: `Transmis le ${formaterDate(transfert.date)}`,
            contenu: formaterXml(String(contenu ?? '')),
          }),
        )
        .catch(() => {
          toast.error('Le fichier du lot est introuvable');
        })
        .finally(() => setEnCours(null));
    },
    [afficher],
  );

  const afficherAccuse = React.useCallback(
    (transfert: TransfertInsee, envoi: EnvoiInsee, rang: number) =>
      afficher({
        titre: 'Accusé de réception de l’INSEE',
        sousTitre: `Lot n°${transfert.numero} · Tentative n°${rang} du ${formaterDate(envoi.dateDepot)}`,
        contenu: formaterXml(envoi.accuseReception ?? ''),
      }),
    [afficher],
  );

  const devalider = React.useCallback(() => {
    if (!aDevalider) return;

    const transfert = aDevalider;
    setADevalider(null);
    setEnCours(transfert.identifiant);
    request
      .delete(getInseePacsTransfert(transfert.identifiant))
      .then(() => {
        toast.success(
          `Lot n°${transfert.numero} dévalidé : ses PACS sont de nouveau à transmettre.`,
        );
        onChange?.();
      })
      .catch((error: any) => {
        toast.error(
          messageErreur(error) ??
            'Une erreur est survenue lors de la dévalidation du lot',
        );
      })
      .finally(() => {
        setEnCours(null);
        charger();
      });
  }, [aDevalider, charger, onChange]);

  const renderStatut = (statut: number | null) => {
    const config = _.isNil(statut) ? null : STATUTS[statut];

    return (
      <Label color={config?.couleur ?? 'grey'} size="small" basic>
        {config?.libelle ?? 'Non envoyé'}
      </Label>
    );
  };

  const renderActions = (transfert: TransfertInsee) => {
    const occupe = enCours === transfert.identifiant;

    return (
      <div
        style={{ display: 'flex', gap: '0.3em', justifyContent: 'flex-end' }}
      >
        <Button
          basic
          size="mini"
          icon="file code outline"
          content="Fichier"
          title="Consulter le fichier XML transmis"
          loading={occupe}
          disabled={occupe}
          onClick={() => afficherFichier(transfert)}
          data-testid={`insee-historique-fichier-${transfert.identifiant}`}
        />
        <Button
          basic
          color="red"
          size="mini"
          icon="undo"
          content="Dévalider"
          title="Annuler le lot : ses PACS redeviennent à transmettre"
          disabled={occupe}
          onClick={() => setADevalider(transfert)}
          data-testid={`insee-historique-devalider-${transfert.identifiant}`}
        />
      </div>
    );
  };

  const renderTableau = () => {
    if (_.isEmpty(transferts)) {
      return (
        <Message
          info
          icon="info circle"
          content="Aucun transfert INSEE de PACS n'a encore été effectué."
        />
      );
    }

    return (
      <div style={styles.tableau}>
        <Table celled compact data-testid="insee-historique-tableau">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Transferts effectués</Table.HeaderCell>
              <Table.HeaderCell>Statut</Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {_.map(transferts, (transfert) => {
              const deplie = _.includes(deplies, transfert.identifiant);
              return (
                <React.Fragment key={transfert.identifiant}>
                  <Table.Row
                    active
                    data-testid={`insee-historique-lot-${transfert.identifiant}`}
                  >
                    <Table.Cell
                      style={{ cursor: 'pointer', fontWeight: 700 }}
                      onClick={() => basculer(transfert.identifiant)}
                    >
                      <Icon name={deplie ? 'chevron down' : 'chevron right'} />
                      {`Transfert n°${transfert.numero} du ${formaterDate(transfert.date)}`}
                      {transfert.utilisateur && (
                        <span style={{ fontWeight: 400 }}>
                          {` par ${transfert.utilisateur}`}
                        </span>
                      )}
                      <Label
                        circular
                        basic
                        size="small"
                        title="Tentatives d'envoi"
                        style={{ marginLeft: '0.6em' }}
                      >
                        {transfert.envois.length}
                      </Label>
                    </Table.Cell>
                    <Table.Cell collapsing>
                      {renderStatut(transfert.statut)}
                    </Table.Cell>
                    <Table.Cell collapsing>
                      {renderActions(transfert)}
                    </Table.Cell>
                  </Table.Row>
                  {deplie &&
                    transfert.envois.map((envoi, index) => (
                      <Table.Row
                        key={envoi.identifiant}
                        data-testid={`insee-historique-envoi-${envoi.identifiant}`}
                      >
                        <Table.Cell style={{ paddingLeft: '2.5em' }}>
                          {`Tentative n°${index + 1} du ${formaterDate(envoi.dateDepot)}`}
                          <div style={{ fontSize: '0.9em', color: 'grey' }}>
                            {envoi.libelleStatut}
                          </div>
                        </Table.Cell>
                        <Table.Cell collapsing>
                          {renderStatut(envoi.statut)}
                        </Table.Cell>
                        <Table.Cell collapsing textAlign="right">
                          {estAccuseXml(envoi.accuseReception) ? (
                            <button
                              type="button"
                              style={STYLE_LIEN}
                              onClick={() =>
                                afficherAccuse(transfert, envoi, index + 1)
                              }
                              data-testid={`insee-historique-accuse-${envoi.identifiant}`}
                            >
                              Accusé de réception
                            </button>
                          ) : (
                            // Échec : réponse brute du SDFI (« Erreur 404 »…) ; « OK » sans intérêt
                            envoi.accuseReception !== 'OK' && (
                              <span
                                style={{ fontSize: '0.9em', color: 'grey' }}
                              >
                                {envoi.accuseReception}
                              </span>
                            )
                          )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                </React.Fragment>
              );
            })}
          </Table.Body>
        </Table>
      </div>
    );
  };

  const renderPanneau = () => (
    <Sidebar
      as={Segment}
      animation="overlay"
      direction="right"
      width="very wide"
      visible={panneauOuvert}
      onHide={() => setPanneauOuvert(false)}
      data-testid="insee-historique-panneau"
      style={{
        margin: 0,
        padding: '1.5em',
        border: 'none',
        borderRadius: 0,
        backgroundColor: '#f7f7f8',
        boxShadow: '-4px 0 12px rgba(34, 36, 38, 0.12)',
      }}
    >
      {consultation && (
        <>
          <Icon
            name="close"
            link
            aria-label="Fermer"
            onClick={() => setPanneauOuvert(false)}
            style={{ position: 'absolute', top: '1em', right: '1em' }}
          />
          <Header as="h3" style={{ marginRight: '1.5em' }}>
            {consultation.titre}
            <Header.Subheader>{consultation.sousTitre}</Header.Subheader>
          </Header>
          <Segment
            as="pre"
            style={{
              margin: 0,
              maxHeight: 'calc(50vh)',
              overflow: 'auto',
              fontSize: '0.85em',
              whiteSpace: 'pre',
            }}
          >
            {consultation.contenu}
          </Segment>
        </>
      )}
    </Sidebar>
  );

  return mettreEnPage({
    titre: 'Historique des transferts INSEE',
    corps: (
      <Sidebar.Pushable style={styles.pushable}>
        {renderPanneau()}
        <Sidebar.Pusher style={styles.pusher}>
          {loading && !transferts ? (
            <Dimmer active inverted>
              <Loader content="Chargement de l'historique..." />
            </Dimmer>
          ) : (
            renderTableau()
          )}
        </Sidebar.Pusher>
      </Sidebar.Pushable>
    ),
    dialogues: (
      <Confirm
        open={!_.isNil(aDevalider)}
        size="small"
        header={`Dévalider le lot n°${aDevalider?.numero ?? ''} ?`}
        content="Les PACS de ce lot redeviendront à transmettre à l'INSEE. Le lot et ses tentatives d'envoi seront supprimés de l'historique."
        cancelButton="Annuler"
        confirmButton="Dévalider"
        onCancel={() => setADevalider(null)}
        onConfirm={devalider}
      />
    ),
  });
};

export default InseeHistorique;
